import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useListUiState } from '../hooks/useListUiState'
import { useScrollRestoration } from '../hooks/useScrollRestoration'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { getTeacherInsights, clearTeacherInsightsCache } from '../services/teacherInsightsService'
import { exportTeacherInsightsCsv } from '../utils/teacherCsvExport'
import WeeklyStats from '../components/WeeklyStats'
import TrendIndicators from '../components/TrendIndicators'
import PeriodComparison from '../components/PeriodComparison'
import SkillsRadar from '../components/SkillsRadar'
import TeacherSkillsHeatmap from '../components/TeacherSkillsHeatmap'
import './TeacherInsights.css'

function buildRadarData(skillRows) {
  const toAxis = [
    { key: 'function-T', axis: 'Tonique' },
    { key: 'function-SD', axis: 'Sous-dom.' },
    { key: 'function-D', axis: 'Dominante' },
    { key: 'cadence-perfect', axis: 'Cadence parfaite' },
    { key: 'cadence-half', axis: 'Demi cadence' }
  ]
  return toAxis.map((target) => {
    const match = (skillRows || []).find((row) => row.key === target.key)
    return {
      axis: target.axis,
      value: match?.successRate || 0
    }
  })
}

function formatShortDateLabel(dateKey) {
  if (!dateKey) return ''
  const d = new Date(dateKey)
  if (Number.isNaN(d.getTime())) return dateKey
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit' }).format(d)
}

function formatLongDateLabel(dateKey) {
  if (!dateKey) return ''
  const d = new Date(dateKey)
  if (Number.isNaN(d.getTime())) return dateKey
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(d)
}


const INSIGHTS_LIST_FIELDS = {
  period: { type: 'string', default: '30d' },
  classId: { type: 'string', default: '' },
  scope: { type: 'string', default: 'mine' },
  studentFilter: { type: 'string', default: 'all' },
  q: { type: 'string', default: '', debounce: true },
  establishment: { type: 'string', default: '' },
  classRef: { type: 'string', default: '' },
  level: { type: 'string', default: '' },
  studentSort: { type: 'string', default: 'risk' },
}

function TeacherInsights() {
  const { user, userData, isAdminTeacher } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    values: listValues,
    inputValues: listInput,
    setField: setListField,
    setDraft: setListDraft,
  } = useListUiState({ fields: INSIGHTS_LIST_FIELDS, debounceMs: 300 })
  const period = listValues.period
  const setPeriod = (v) => setListField('period', v)
  const classFilter = listValues.classId
  const setClassFilter = (v) => setListField('classId', v)
  const scope = listValues.scope
  const setScope = (v) => setListField('scope', v)
  const studentFilter = listValues.studentFilter
  const setStudentFilter = (v) => setListField('studentFilter', v)
  const studentSearch = listInput.q
  const setStudentSearch = (v) => setListDraft('q', v)
  const studentEstablishmentFilter = listValues.establishment
  const setStudentEstablishmentFilter = (v) => setListField('establishment', v)
  const studentClassRefFilter = listValues.classRef
  const setStudentClassRefFilter = (v) => setListField('classRef', v)
  const studentLevelFilter = listValues.level
  const setStudentLevelFilter = (v) => setListField('level', v)
  const studentSort = listValues.studentSort
  const setStudentSort = (v) => setListField('studentSort', v)
  const [loading, setLoading] = useState(true)
  useScrollRestoration('teacher-insights', { ready: !loading })
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [insights, setInsights] = useState(null)

  const loadInsights = async (force = false) => {
    if (!user?.uid) return
    if (force) {
      clearTeacherInsightsCache()
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError('')
    try {
      const data = await getTeacherInsights(user.uid, {
        period,
        teacherClassId: classFilter || '',
        scope,
        isAdminTeacher
      })
      setInsights(data)
    } catch (err) {
      console.error('TeacherInsights load:', err)
      setError(err.message || 'Impossible de charger les donnees.')
      setInsights(null)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!user || userData?.role !== 'teacher') {
      navigate('/dashboard')
      return
    }
  }, [user, userData?.role, navigate])

  useEffect(() => {
    loadInsights(false)
  }, [user?.uid, period, classFilter, scope, isAdminTeacher])

  useEffect(() => {
    if (!isAdminTeacher && scope === 'all') setScope('mine')
  }, [isAdminTeacher, scope])

  const filteredStudents = useMemo(() => {
    const source = insights?.lists?.allStudents || []
    let list = [...source]
    if (studentFilter === 'risk') list = list.filter((s) => s.riskScore >= 40)
    if (studentSearch.trim()) {
      const q = studentSearch.toLowerCase().trim()
      list = list.filter((s) =>
        (s.studentName || '').toLowerCase().includes(q)
        || (s.className || '').toLowerCase().includes(q)
        || (s.establishment || '').toLowerCase().includes(q)
        || (s.studentClass || '').toLowerCase().includes(q)
      )
    }
    if (studentEstablishmentFilter) {
      list = list.filter((s) => (s.establishment || '') === studentEstablishmentFilter)
    }
    if (studentClassRefFilter) {
      list = list.filter((s) => (s.studentClass || '') === studentClassRefFilter)
    }
    if (studentLevelFilter) {
      list = list.filter((s) => String(s.studentLevel || '') === studentLevelFilter)
    }

    const compareBy = {
      risk: (a, b) => Number(b.riskScore || 0) - Number(a.riskScore || 0),
      score: (a, b) => Number(b.averageScore || 0) - Number(a.averageScore || 0),
      attempts: (a, b) => Number(b.attemptCount || 0) - Number(a.attemptCount || 0),
      name: (a, b) => (a.studentName || '').localeCompare(b.studentName || '', 'fr', { sensitivity: 'base' })
    }
    list.sort(compareBy[studentSort] || compareBy.risk)
    return list
  }, [
    insights?.lists?.allStudents,
    studentFilter,
    studentSearch,
    studentEstablishmentFilter,
    studentClassRefFilter,
    studentLevelFilter,
    studentSort
  ])

  const studentFilterOptions = useMemo(() => {
    const source = insights?.lists?.allStudents || []
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }))
    return {
      establishments: uniq(source.map((s) => s.establishment || '')),
      classes: uniq(source.map((s) => s.studentClass || '')),
      levels: uniq(source.map((s) => s.studentLevel || ''))
    }
  }, [insights?.lists?.allStudents])

  const summaryRows = useMemo(() => {
    const kpi = insights?.kpis
    if (!kpi) return []
    return [
      { label: 'Taux de rendu global', value: `${kpi.submissionRate}%` },
      { label: 'Retards', value: kpi.overdueCount },
      { label: scope === 'all' ? 'Score moyen (global)' : 'Score median', value: `${kpi.medianScore}%` },
      { label: 'Tentatives (periode)', value: insights?.charts?.daily?.reduce((sum, d) => sum + Number(d.participation || 0), 0) || 0 },
      { label: 'Eleves actifs (7j)', value: kpi.activeStudents7d },
      { label: 'Eleves a risque', value: kpi.atRiskCount }
    ]
  }, [insights?.kpis, insights?.charts?.daily, scope])

  if (!user || userData?.role !== 'teacher') return null

  return (
    <div className="teacher-insights-page">
      <header className="teacher-insights-header">
        <button type="button" className="teacher-insights-back" onClick={() => navigate('/dashboard')}>
          ← Retour au tableau de bord
        </button>
        <h1 className="teacher-insights-title">Cockpit professeur</h1>
        <p className="teacher-insights-intro">
          Pilotage global des classes, devoirs et alertes pedagogiques.
        </p>
      </header>

      <section className="teacher-insights-filters">
        <label>
          Periode
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="7d">7 jours</option>
            <option value="30d">30 jours</option>
            <option value="90d">90 jours</option>
            <option value="trimester">Trimestre</option>
          </select>
        </label>
        {isAdminTeacher && (
          <label>
            Perimetre
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value)
                setClassFilter('')
              }}
            >
              <option value="mine">Mes classes</option>
              <option value="all">Tous les eleves (global)</option>
            </select>
          </label>
        )}
        <label>
          Classe
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            disabled={scope === 'all'}
          >
            <option value="">Toutes</option>
            {(insights?.filters?.classes || []).map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </label>
        <label>
          Eleves
          <select value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)}>
            <option value="all">Tous</option>
            <option value="risk">A risque uniquement</option>
          </select>
        </label>
        <button type="button" onClick={() => loadInsights(true)} disabled={refreshing}>
          {refreshing ? 'Actualisation…' : 'Actualiser'}
        </button>
        <button
          type="button"
          onClick={() => exportTeacherInsightsCsv({
            summaryRows,
            studentRows: filteredStudents,
            assignmentRows: insights?.lists?.assignments || []
          })}
          disabled={!insights}
        >
          Export CSV
        </button>
      </section>

      {loading ? (
        <div className="teacher-insights-loading">
          <div className="spinner" />
          <p>Chargement des insights…</p>
        </div>
      ) : error ? (
        <div className="teacher-insights-error">{error}</div>
      ) : !insights ? (
        <div className="teacher-insights-empty">Aucune donnee disponible.</div>
      ) : (
        <>
          {(insights?.meta?.warnings || []).map((warning) => (
            <div key={warning} className="teacher-insights-note">{warning}</div>
          ))}

          <section className="teacher-insights-kpis">
            {summaryRows.map((row) => (
              <article key={row.label} className="teacher-insights-kpi-card">
                <h3>{row.label}</h3>
                <p>{row.value}</p>
              </article>
            ))}
          </section>

          <section className="teacher-insights-grid">
            <article className="teacher-insights-panel teacher-insights-panel--at-risk">
              <h2>Eleves a suivre</h2>
              {insights.lists.atRiskStudents.length === 0 ? (
                <p className="teacher-insights-muted">Aucune alerte eleve sur cette periode.</p>
              ) : (
                <ul className="teacher-insights-alert-list teacher-insights-alert-list--scrollable">
                  {insights.lists.atRiskStudents.slice(0, 12).map((student) => (
                    <li key={student.studentId}>
                      <div>
                        <strong>{student.studentName}</strong>
                        <span>{student.className}</span>
                      </div>
                      <div>
                        <span className={`teacher-insights-risk teacher-insights-risk-${student.riskLabel}`}>
                          {student.riskLabel}
                        </span>
                        <small>{student.alertReasons.join(' · ') || 'A surveiller'}</small>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </article>

            <article className="teacher-insights-panel">
              <h2>Distribution des scores</h2>
              <div className="teacher-insights-chart">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={insights.charts.scoreDistribution}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="key" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4f46e5" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="teacher-insights-panel">
              <h2>Tendance journaliere</h2>
              <div className="teacher-insights-chart">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={insights.charts.daily}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatShortDateLabel}
                      minTickGap={22}
                      interval="preserveStartEnd"
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip labelFormatter={formatLongDateLabel} />
                    <Line yAxisId="left" type="monotone" dataKey="averageScore" stroke="#0ea5e9" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="participation" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="teacher-insights-panel">
              <h2>Recommandations</h2>
              {insights.lists.recommendations.length === 0 ? (
                <p className="teacher-insights-muted">Aucune recommandation prioritaire pour l’instant.</p>
              ) : (
                <ul className="teacher-insights-reco-list">
                  {insights.lists.recommendations.map((item) => (
                    <li key={item.title}>
                      {item.type === 'assignment' ? (
                        <article className="teacher-insights-reco-assignment-card">
                          <div className="teacher-insights-reco-assignment-layout">
                            <div className="teacher-insights-reco-assignment-visual">
                              {item.exerciseThumbnail ? (
                                <img
                                  src={item.exerciseThumbnail}
                                  alt={item.assignmentTitle || item.title}
                                  className="teacher-insights-reco-thumb"
                                />
                              ) : (
                                <div className="teacher-insights-reco-thumb teacher-insights-reco-thumb--placeholder" aria-hidden="true">♪</div>
                              )}
                            </div>
                            <div className="teacher-insights-reco-assignment-content">
                              <div className="teacher-insights-reco-assignment-header">
                                <span className="teacher-insights-reco-badge">Devoir a revoir</span>
                                {item.className ? <span className="teacher-insights-reco-class">{item.className}</span> : null}
                              </div>
                              <strong className="teacher-insights-reco-assignment-title">{item.assignmentTitle || item.title}</strong>
                              <div className="teacher-insights-reco-assignment-metrics">
                                <span>Score median: {item.medianScore ?? '—'}%</span>
                                <span>Taux de rendu: {item.submissionRate ?? '—'}%</span>
                              </div>
                            </div>
                          </div>
                        </article>
                      ) : (
                        <>
                          <strong>{item.title}</strong>
                          <p>{item.detail}</p>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          </section>

          <section className="teacher-insights-grid teacher-insights-grid-bottom">
            <article className="teacher-insights-panel">
              <h2>Heatmap competences</h2>
              <TeacherSkillsHeatmap rows={insights.skillsHeatmapRows} />
            </article>
            <article className="teacher-insights-panel">
              <h2>Radar de classe</h2>
              <SkillsRadar data={buildRadarData(insights.skillsHeatmapRows)} />
            </article>
          </section>

          <section className="teacher-insights-grid teacher-insights-grid-bottom">
            <article className="teacher-insights-panel">
              <h2>Activite recente</h2>
              <WeeklyStats attempts={insights.attempts} />
              <PeriodComparison attempts={insights.attempts} />
            </article>
            <article className="teacher-insights-panel">
              <h2>Tendance pedagogique</h2>
              <TrendIndicators attempts={insights.attempts} />
            </article>
          </section>

          <section className="teacher-insights-panel">
            <h2>Devoirs: difficulte percue</h2>
            {(insights.lists.assignments || []).length === 0 ? (
              <p className="teacher-insights-muted">Aucun devoir sur la periode.</p>
            ) : (
              <div className="teacher-insights-table-wrap">
                <table className="teacher-insights-table">
                  <thead>
                    <tr>
                      <th>Devoir</th>
                      <th>Classe</th>
                      <th>Rendus</th>
                      <th>Taux rendu</th>
                      <th>Score median</th>
                      <th>Retards</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(insights.lists.assignments || []).map((row) => (
                      <tr key={row.assignmentId}>
                        <td>{row.title}</td>
                        <td>{row.className}</td>
                        <td>{row.submittedCount}/{row.classSize}</td>
                        <td>{row.submissionRate}%</td>
                        <td>{row.medianScore}%</td>
                        <td>{row.overdueCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="teacher-insights-panel">
            <h2>Liste eleves ({filteredStudents.length})</h2>
            <div className="teacher-insights-student-filters">
              <label>
                Recherche
                <input
                  type="text"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Nom, classe, etablissement..."
                />
              </label>
              <label>
                Etablissement
                <select value={studentEstablishmentFilter} onChange={(e) => setStudentEstablishmentFilter(e.target.value)}>
                  <option value="">Tous</option>
                  {studentFilterOptions.establishments.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Classe (ref)
                <select value={studentClassRefFilter} onChange={(e) => setStudentClassRefFilter(e.target.value)}>
                  <option value="">Toutes</option>
                  {studentFilterOptions.classes.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Niveau
                <select value={studentLevelFilter} onChange={(e) => setStudentLevelFilter(e.target.value)}>
                  <option value="">Tous</option>
                  {studentFilterOptions.levels.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Trier
                <select value={studentSort} onChange={(e) => setStudentSort(e.target.value)}>
                  <option value="risk">Risque</option>
                  <option value="score">Score</option>
                  <option value="attempts">Tentatives</option>
                  <option value="name">Nom</option>
                </select>
              </label>
            </div>
            {filteredStudents.length === 0 ? (
              <p className="teacher-insights-muted">Aucun eleve dans ce filtre.</p>
            ) : (
              <div className="teacher-insights-table-wrap">
                <table className="teacher-insights-table">
                  <thead>
                    <tr>
                      <th>Eleve</th>
                      <th>Classe prof</th>
                      <th>Etablissement</th>
                      <th>Classe (ref)</th>
                      <th>Niveau</th>
                      <th>Score</th>
                      <th>Tentatives</th>
                      <th>Risque</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.slice(0, 200).map((student) => (
                      <tr key={student.studentId}>
                        <td>{student.studentName}</td>
                        <td>{student.className || '—'}</td>
                        <td>{student.establishment || '—'}</td>
                        <td>{student.studentClass || '—'}</td>
                        <td>{student.studentLevel || '—'}</td>
                        <td>{student.averageScore || 0}%</td>
                        <td>{student.attemptCount || 0}</td>
                        <td>{student.riskLabel || 'stable'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredStudents.length > 200 && (
                  <p className="teacher-insights-muted">Affichage limite a 200 eleves pour preserver les performances.</p>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

export default TeacherInsights
